import React, { useState, useRef, Fragment } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { CalendarIcon, ArrowRightIcon, TagIcon, ChevronDownIcon, ExternalLinkIcon, NewspaperIcon, TrophyIcon, InstagramIcon, FacebookIcon, PlayCircleIcon, XIcon, Share2Icon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { BLOG_POSTS, BlogPost, SPORTS_COMPOSITE_IMAGES } from '../data/blog-posts';
import { getCategoryColor, getPostNum, resolveTranslatedField, translateCategory } from '../utils/blog-helpers';
// ============================================================
//  Blog posts data is now in data/blog-posts.ts
//  Edit that file to add or modify blog posts.
// ============================================================
const ALL_CATEGORIES = ['All', 'Political', 'Immigration', 'Business', 'Note Worthy', 'Events', 'Arts & Entertainment', 'Sports'];
const GRADIENT_FALLBACKS: Record<string, string> = {
  Political: 'from-haa-blue via-blue-700 to-haa-navy',
  Immigration: 'from-haa-skyblue to-haa-midblue',
  Business: 'from-emerald-500 to-emerald-800',
  'Note Worthy': 'from-haa-midblue to-haa-navy',
  'Arts & Entertainment': 'from-haa-navy to-blue-950',
  Events: 'from-blue-400 to-haa-midblue',
  Sports: 'from-orange-500 to-red-600'
};
function getGradient(category: string) {
  return GRADIENT_FALLBACKS[category] ?? 'from-haa-blue to-haa-navy';
}
// Story Modal Component
function StoryModal({
  isOpen,
  onClose,
  post,
  translatedTitle,
  translatedDate,
  translatedFullStory,
  isEs,
  t
}: {
  isOpen: boolean;
  onClose: () => void;
  post: BlogPost;
  translatedTitle: string;
  translatedDate: string;
  translatedFullStory?: string;
  isEs: boolean;
  t: any;
}) {
  const [expandedPlayer, setExpandedPlayer] = useState<string | null>(null);
  return <AnimatePresence data-id="element-2971" data-mp="37ngee9wjlju9">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-2972" data-mp="37moh8unare1v">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-2973" data-mp="3tnf2okrslfdr">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white sticky top-0 z-10" data-id="element-2974" data-mp="2apisazol6oke">
              <span className={`font-nunito text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-2975" data-mp="25rh6w83oud5r">
                {post.category}
              </span>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600" aria-label="Close" data-id="element-2976" data-mp="1vuckc75xkfof">
                <XIcon className="w-5 h-5" data-id="element-2977" data-mp="1i0btdzl7zwym" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto" data-id="element-2978" data-mp="1d20hzkfa1eea">
              {post.image && !(post.players && post.players.length > 0) && <div className="w-full h-80 sm:h-96 relative" data-id="element-2979" data-mp="181w3fzxxitqp">
                  <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover" style={{
              objectPosition: post.imagePosition || 'center top'
            }} data-id="element-2980" data-mp="140akpxyprl8l" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" data-id="element-2981" data-mp="1jzy08ggecyjs" />
                </div>}

              {/* Video Embeds — shown at top when no image */}
              {!post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && <div className="w-full" data-id="element-2982" data-mp="1f0uusuwi8qba">
                    {post.videoEmbeds.map((video, vi) => <div key={vi} className="bg-black relative" data-id="element-2983" data-mp="111v65n8zsbf4" data-mpi={vi}>
                        {video.youtubeId ? <div className="relative w-full" style={{
                paddingBottom: '56.25%'
              }} data-id="element-2984" data-mp="1525l3peric64">
                            <iframe src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?rel=0&modestbranding=1&playsinline=1`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen className="absolute inset-0 w-full h-full" style={{
                  border: 0
                }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-2985" data-mp="nsa1yil80h6a" />
                            {/* Fallback thumbnail link */}
                            <a href={`https://www.youtube.com/watch?v=${video.youtubeId}`} target="_blank" rel="noopener noreferrer" className="absolute inset-0 -z-10 flex flex-col items-center justify-center bg-black" data-id="element-2986" data-mp="3i8c42vl4ia8t">
                              <img src={`https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`} alt={video.label} className="absolute inset-0 w-full h-full object-cover" data-id="element-2987" data-mp="1m7aagm5eqre2" />
                              <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-2" data-id="element-2988" data-mp="203dyfxpou3i3">
                                <PlayCircleIcon className="w-14 h-14 text-white/90 drop-shadow-lg" data-id="element-2989" data-mp="29ui426th4c09" />
                                <span className="font-nunito text-white text-xs font-bold bg-red-600 px-3 py-1.5 rounded-full shadow-lg" data-id="element-2990" data-mp="2eumjdtinb0ow">
                                  Watch on YouTube
                                </span>
                              </div>
                            </a>
                          </div> : null}
                      </div>)}
                  </div>}

              <div className="p-6 sm:p-8" data-id="element-2991" data-mp="771mtgtqqn1i">
                <div className="flex items-center gap-2 mb-4" data-id="element-2992" data-mp="36hoi9xfldds">
                  <CalendarIcon className="w-4 h-4 text-haa-blue" data-id="element-2993" data-mp="38vjkis42plc" />
                  <span className="font-nunito text-gray-500 text-sm font-semibold" data-id="element-2994" data-mp="3uedsok2tn5el">
                    {translatedDate}
                  </span>
                </div>

                <h2 className="font-bebas text-3xl sm:text-4xl text-haa-navy tracking-wider leading-tight mb-6" data-id="element-2995" data-mp="qvtxb8oujjfk">
                  {translatedTitle}
                </h2>

                {translatedFullStory && <div className="prose prose-lg max-w-none font-nunito text-gray-600 leading-relaxed whitespace-pre-line mb-8" data-id="element-2996" data-mp="h7c174rii0i9">
                    {translatedFullStory}
                  </div>}

                {/* Video Embeds — shown after story when post has image */}
                {post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && <div className="mb-8 space-y-4" data-id="element-2997" data-mp="mz51fj7z0s7b">
                      {post.videoEmbeds.map((video, vi) => <div key={vi} className="rounded-xl overflow-hidden shadow-md bg-black relative" data-id="element-2998" data-mp="8b1r5xmwaucp" data-mpi={vi}>
                          {video.youtubeId ? <div className="relative w-full" style={{
                  paddingBottom: '56.25%'
                }} data-id="element-2999" data-mp="15x7inbua1kuk">
                              <iframe src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?rel=0&modestbranding=1&playsinline=1`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen className="absolute inset-0 w-full h-full" style={{
                    border: 0
                  }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-3000" data-mp="vxqeiyuobf0p" />
                              <a href={`https://www.youtube.com/watch?v=${video.youtubeId}`} target="_blank" rel="noopener noreferrer" className="absolute inset-0 -z-10 flex flex-col items-center justify-center bg-black" data-id="element-3001" data-mp="2lgozm85xr6be">
                                <img src={`https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`} alt={video.label} className="absolute inset-0 w-full h-full object-cover" data-id="element-3002" data-mp="22pp5ksjl36b9" />
                                <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-2" data-id="element-3003" data-mp="2bjrsdd5x26z5">
                                  <PlayCircleIcon className="w-14 h-14 text-white/90 drop-shadow-lg" data-id="element-3004" data-mp="26jvn7alhg9i0" />
                                  <span className="font-nunito text-white text-xs font-bold bg-red-600 px-3 py-1.5 rounded-full shadow-lg" data-id="element-3005" data-mp="21mnd7xahnbru">
                                    Watch on YouTube
                                  </span>
                                </div>
                              </a>
                            </div> : null}
                        </div>)}
                    </div>}

                {/* Story Images Gallery */}
                {post.storyImages && post.storyImages.length > 0 && <div className="mb-8 space-y-4" data-id="element-3006" data-mp="13t45724r0jfa">
                    {post.storyImages.map((img, idx) => <div key={idx} className="rounded-xl overflow-hidden shadow-md" data-id="element-3007" data-mp="18q4cthh8xu8i" data-mpi={idx}>
                        <img src={img} alt={`${translatedTitle} — ${idx + 1}`} className="w-full h-auto object-cover" data-id="element-3008" data-mp="1ipbpe2g5pkuk" />
                      </div>)}
                  </div>}

                {/* Players */}
                {post.players && post.players.length > 0 && <div className="space-y-3 mb-8" data-id="element-3009" data-mp="zqhdd182uyi3">
                    <h3 className="font-bebas text-2xl text-haa-navy tracking-wider mb-4" data-id="element-3010" data-mp="zs240fr9txao">
                      {isEs ? 'Jugadores Destacados' : 'Featured Players'}
                    </h3>
                    {post.players.map(player => {
                const isExpanded = expandedPlayer === player.name;
                const playerKeyMap: Record<string, string> = {
                  'Oscar Avilez': 'avilez',
                  'David Ruíz': 'ruiz',
                  'Joseph Rosales': 'rosales',
                  'Andy Nájar': 'najar',
                  'Bryan Acosta': 'acosta'
                };
                const playerKey = playerKeyMap[player.name] ?? '';
                const translatedBio = playerKey ? t(`news.player.${playerKey}.bio`) : player.bio;
                const translatedPosition = player.position === 'Midfielder' ? t('news.player.position.midfielder') : player.position === 'Defender' ? t('news.player.position.defender') : player.position;
                return <div key={player.name} className="rounded-xl border border-gray-200 overflow-hidden bg-gray-50" data-id="element-3011" data-mp="14846u34f87i6">
                          <button onClick={() => setExpandedPlayer(isExpanded ? null : player.name)} className="w-full flex items-center gap-4 px-4 py-3 hover:bg-gray-100 transition-colors text-left" aria-expanded={isExpanded} data-id="element-3012" data-mp="v8h2b8gealkw">
                            {player.image ? <img src={player.image} alt={player.name} className="w-16 h-16 rounded-full object-cover object-top shadow-md flex-shrink-0 border-2 border-white" data-id="element-3013" data-mp="1e6l2vei7vcb3" /> : <div className={`w-16 h-16 rounded-full ${player.teamColor} flex items-center justify-center text-white font-nunito font-bold text-lg shadow-md flex-shrink-0`} data-id="element-3014" data-mp="n04nljrckwpw">
                                {player.name.split(' ').map(n => n[0]).join('')}
                              </div>}
                            <div className="flex-1 min-w-0" data-id="element-3015" data-mp="10rasjvdebmk6">
                              <p className="font-nunito text-sm font-bold text-haa-navy leading-tight" data-id="element-3016" data-mp="10tvvyotceak1">
                                {player.name}
                                <span className="text-gray-400 font-normal ml-1.5" data-id="element-3017" data-mp="s0ah75h6gtam">
                                  #{player.number}
                                </span>
                              </p>
                              <p className="font-nunito text-xs text-gray-500 mt-0.5" data-id="element-3018" data-mp="1jplziggxbf01">
                                {translatedPosition} · {player.team}
                              </p>
                            </div>
                            <motion.span animate={{
                      rotate: isExpanded ? 180 : 0
                    }} transition={{
                      duration: 0.2
                    }} className="flex-shrink-0" data-id="element-3019" data-mp="37d1f77mv6ohu">
                              <ChevronDownIcon className="w-4 h-4 text-gray-400" data-id="element-3020" data-mp="3qa2miz3w50oy" />
                            </motion.span>
                          </button>
                          <AnimatePresence initial={false} data-id="element-3021" data-mp="2y1pbtxd15xd">
                            {isExpanded && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.25,
                      ease: [0.16, 1, 0.3, 1]
                    }} className="overflow-hidden" data-id="element-3022" data-mp="7xwe9rxkrlfs">
                                <div className="px-4 pb-4 pt-1 border-t border-gray-200" data-id="element-3023" data-mp="31nrgx216uw0w">
                                  <p className="font-nunito text-sm text-gray-600 leading-relaxed" data-id="element-3024" data-mp="2wqhw5zoqv8je">
                                    {translatedBio}
                                  </p>
                                  <a href={player.profileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-3 font-nunito text-xs font-bold text-haa-midblue hover:text-haa-blue transition-colors" data-id="element-3025" data-mp="12c74zkkog2ag">
                                    {t('news.player.viewProfile')}
                                    <ExternalLinkIcon className="w-3 h-3" data-id="element-3026" data-mp="2v7rncxai1x3x" />
                                  </a>
                                </div>
                              </motion.div>}
                          </AnimatePresence>
                        </div>;
              })}
                  </div>}

                {/* Video Embeds — shown after story when there is an image */}
                {post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && <div className="mb-8" data-id="element-3027" data-mp="38dchl6wljvq5">
                      <div className="flex items-center gap-2 mb-4" data-id="element-3028" data-mp="38cav86a4rmt4">
                        <PlayCircleIcon className="w-5 h-5 text-amber-600" data-id="element-3029" data-mp="33dzlrwnqzebf" />
                        <h3 className="font-bebas text-2xl text-haa-navy tracking-wider" data-id="element-3030" data-mp="2ynsvlsdl7lr2">
                          {isEs ? 'Videos' : 'Videos'}
                        </h3>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3031" data-mp="2opmsj7g1o9km">
                        {post.videoEmbeds.map((video, vi) => <div key={vi} className="rounded-xl overflow-hidden shadow-md bg-black" data-id="element-3032" data-mp="2kn8er1r8g007" data-mpi={vi}>
                            {video.youtubeId ? <div className="relative w-full" style={{
                    paddingBottom: '56.25%'
                  }} data-id="element-3033" data-mp="2ary820pw34g3">
                                <iframe src={`https://www.youtube.com/embed/${video.youtubeId}`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" data-id="element-3034" data-mp="gh899hgkl1wo" />
                              </div> : video.facebookUrl ? <a href={video.facebookUrl} target="_blank" rel="noopener noreferrer" className="relative block w-full bg-gradient-to-br from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 transition-colors" style={{
                    paddingBottom: '56.25%'
                  }} data-id="element-3035" data-mp="7gs9ahz1ccwk">
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2" data-id="element-3036" data-mp="337ok6nhe4ojx">
                                  <FacebookIcon className="w-8 h-8 text-blue-400" data-id="element-3037" data-mp="3807tv9cyizjg" />
                                  <span className="font-nunito text-white text-sm font-semibold" data-id="element-3038" data-mp="3bzzxr8reohih">
                                    {video.label}
                                  </span>
                                  <span className="font-nunito text-white/50 text-xs" data-id="element-3039" data-mp="2kb55zjrqcagi">
                                    {isEs ? 'Ver en Facebook' : 'Watch on Facebook'}
                                  </span>
                                </div>
                              </a> : null}
                            <div className="px-4 py-3 bg-gray-900" data-id="element-3040" data-mp="8jrorsqb8mpr">
                              <p className="font-nunito text-white/80 text-xs font-semibold truncate" data-id="element-3041" data-mp="iohu88kxa8yq">
                                {video.label}
                              </p>
                            </div>
                          </div>)}
                      </div>
                    </div>}

                {/* Social Links */}
                {post.socialLinks && post.socialLinks.length > 0 && <div className="mb-8 pt-6 border-t border-gray-100" data-id="element-3042" data-mp="3w4z4hguvafdp">
                    <h3 className="font-bebas text-xl text-haa-navy tracking-wider mb-4" data-id="element-3043" data-mp="3r41l50y4ffam">
                      {isEs ? 'Síguelos' : 'Follow Them'}
                    </h3>
                    <div className="flex flex-wrap items-center gap-3" data-id="element-3044" data-mp="3i3mqvc36ipuz">
                      {post.socialLinks.map((social, si) => <a key={si} href={social.url} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-nunito text-sm font-bold transition-all duration-200 ${social.platform === 'instagram' ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white hover:shadow-lg hover:shadow-pink-500/25 hover:scale-105' : 'bg-[#1877F2] text-white hover:shadow-lg hover:shadow-blue-500/25 hover:scale-105'}`} aria-label={social.label} data-id="element-3045" data-mp="1kctt8jfi11ys" data-mpi={si}>
                          {social.platform === 'instagram' ? <InstagramIcon className="w-4 h-4" data-id="element-3046" data-mp="1ct2vi14dlos" /> : <FacebookIcon className="w-4 h-4" data-id="element-3047" data-mp="3jn4q7aw5z8tv" />}
                          {social.label}
                        </a>)}
                    </div>
                  </div>}

                {/* Coverage Links */}
                {post.coverageLinks && post.coverageLinks.length > 0 && <div className="pt-6 border-t border-gray-100" data-id="element-3048" data-mp="24m4klxjtp59i">
                    <div className="flex items-center gap-2 mb-3" data-id="element-3049" data-mp="20mnhvbvs6m81">
                      <NewspaperIcon className="w-4 h-4 text-gray-400" data-id="element-3050" data-mp="20ogq2vm1lei5" />
                      <span className="font-nunito text-gray-500 text-sm uppercase tracking-wider font-bold" data-id="element-3051" data-mp="2el3vlms29czo">
                        {t('news.alsoCoveredBy')}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3" data-id="element-3052" data-mp="1lv547kqe93ru">
                      {post.coverageLinks.map((link, i) => <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 font-nunito text-sm font-bold text-haa-midblue hover:text-haa-blue transition-colors border border-gray-200" data-id="element-3053" data-mp="1vnj7wj118kgn" data-mpi={i}>
                          {link.label}
                          <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-3054" data-mp="6f3blpr0jjvf" />
                        </a>)}
                    </div>
                  </div>}
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
// Featured Card
function FeaturedCard({
  post
}: {
  post: BlogPost;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const [modalOpen, setModalOpen] = useState(false);
  const {
    t,
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const postNum = getPostNum(post.id);
  const translatedTitle = resolveTranslatedField(t, postNum, 'title', post.title);
  const translatedExcerpt = resolveTranslatedField(t, postNum, 'excerpt', post.excerpt);
  const translatedDate = resolveTranslatedField(t, postNum, 'date', post.date);
  const translatedFullStory = resolveTranslatedField(t, postNum, 'fullStory', post.fullStory);
  const hasStoryUrl = post.storyUrl && post.storyUrl !== '#';
  const hasFullStory = !!post.fullStory || !!post.players || !!post.videoEmbeds;
  const translatedCategory = translateCategory(t, post.category);
  return <motion.article ref={ref} initial={{
    opacity: 0,
    y: 40
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.8,
    ease: [0.16, 1, 0.3, 1]
  }} className="bg-white rounded-2xl overflow-hidden shadow-xl lg:col-span-2 group" data-id="element-3055" data-mp="2eot25pj56hf8">
      <div className="relative h-64 sm:h-80 overflow-hidden" data-id="element-3056" data-mp="2xd6g2h4jetlj">
        {post.image ? <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
        objectPosition: post.imagePosition || 'center 25%'
      }} data-id="element-3057" data-mp="1qyypb7ytosuz" /> : !post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && post.videoEmbeds[0].youtubeId ? <iframe src={`https://www.youtube.com/embed/${post.videoEmbeds[0].youtubeId}`} title={post.videoEmbeds[0].label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" data-id="element-3058" data-mp="1igd7wfqgi0ax" /> : <div className={`w-full h-full bg-gradient-to-br ${getGradient(post.category)}`} data-id="element-3059" data-mp="us3pzowd5wp2">
            <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 30% 70%, white 1px, transparent 1px)',
          backgroundSize: '25px 25px'
        }} data-id="element-3060" data-mp="zq5b0fdco636" />
          </div>}
        {!(post.videoEmbeds && post.videoEmbeds.length > 0 && !post.image) && <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" data-id="element-3061" data-mp="25vnf8o5qyd8" />}
        <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-3062" data-mp="g1zcknk6cgiu">
          <span className={`font-nunito text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${getCategoryColor(post.category)} mb-3 inline-block`} data-id="element-3063" data-mp="c1fe9gnv76v4">
            {translatedCategory}
          </span>
          {!(post.videoEmbeds && post.videoEmbeds.length > 0 && !post.image) && <p className="font-bebas text-white text-3xl sm:text-4xl leading-tight tracking-wider line-clamp-2" data-id="element-3064" data-mp="ut8b5m621c84">
              {translatedTitle}
            </p>}
        </div>
      </div>

      <div className="p-6 sm:p-8" data-id="element-3065" data-mp="13ty1iacjqaeg">
        <div className="flex items-center gap-3 mb-4" data-id="element-3066" data-mp="13uzeikd4h6yt">
          <div className="w-8 h-1 bg-haa-blue rounded-full" data-id="element-3067" data-mp="2b63yew5zoau9" />
          <span className="font-nunito text-haa-blue text-xs font-bold uppercase tracking-widest" data-id="element-3068" data-mp="2b5u79597jxmz">
            {t('news.featuredStory')}
          </span>
        </div>

        <h2 className="font-bebas text-3xl sm:text-4xl text-haa-navy tracking-wider leading-tight mb-3" data-id="element-3069" data-mp="2k6jwfq3yqprq">
          {translatedTitle}
        </h2>

        <div className="mb-6" data-id="element-3070" data-mp="2p3shbyghx7wo">
          <p className="font-nunito text-gray-600 leading-relaxed" data-id="element-3071" data-mp="1sg5rwkhuapsq">
            {translatedExcerpt}
          </p>

          {/* Story preview — show first 2 paragraphs of fullStory */}
          {translatedFullStory && <div className="relative mt-4" data-id="element-3072" data-mp="21f9alnd3h36p">
              <div className="font-nunito text-gray-500 text-sm leading-relaxed space-y-3" data-id="element-3073" data-mp="21ehembfl53fw">
                {translatedFullStory.split('\n\n').slice(0, 2).map((para, i) => <p key={i} data-id="element-3074" data-mp="1dq81dxwfto0d" data-mpi={i}>{para}</p>)}
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" data-id="element-3075" data-mp="1676zfh1ac2f4" />
            </div>}

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5" data-id="element-3076" data-mp="118x5tav7l9vm">
            {hasStoryUrl ? <a href={post.storyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-haa-blue text-white font-nunito font-bold text-sm px-5 py-2.5 rounded-full hover:bg-haa-midblue transition-colors shadow-md hover:shadow-lg" data-id="element-3077" data-mp="wb416m9k0nxg">
                {t('news.readFullStory')}
                <ExternalLinkIcon className="w-4 h-4" data-id="element-3078" data-mp="dit4591kzk79" />
              </a> : hasFullStory ? <button onClick={() => setModalOpen(true)} className="inline-flex items-center gap-1.5 bg-haa-blue text-white font-nunito font-bold text-sm px-5 py-2.5 rounded-full hover:bg-haa-midblue transition-colors shadow-md hover:shadow-lg" data-id="element-3079" data-mp="8hdh5bfuf3m8">
                {t('news.readFullStory')}
                <ArrowRightIcon className="w-4 h-4" data-id="element-3080" data-mp="2znig08479ra0" />
              </button> : null}

            {post.coverageLinks && post.coverageLinks.length > 0 && <>
                <span className="text-gray-200 select-none hidden sm:inline" data-id="element-3081" data-mp="39lg3gte8jk1k">
                  |
                </span>
                <div className="flex items-center gap-2 flex-wrap" data-id="element-3082" data-mp="2lvbzgkx4xia1">
                  <NewspaperIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" data-id="element-3083" data-mp="2ltix3htk1k7u" />
                  <span className="font-nunito text-gray-400 text-xs uppercase tracking-wider font-semibold" data-id="element-3084" data-mp="2guo3dku1ub8y">
                    {t('news.alsoCoveredBy')}
                  </span>
                  {post.coverageLinks.map((link, i) => <Fragment key={link.url} data-id="element-3085" data-mp="234u9up7ln2sw" data-mpi={i}>
                      {i > 0 && <span className="text-gray-200 select-none" data-id="element-3086" data-mp="1y6j2cl4s14du">·</span>}
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-nunito text-xs font-bold text-haa-midblue hover:text-haa-blue hover:underline transition-colors" data-id="element-3087" data-mp="doibimrstxl0">
                        {link.label}
                        <ExternalLinkIcon className="w-3 h-3" data-id="element-3088" data-mp="8mswkw1ggzrn" />
                      </a>
                    </Fragment>)}
                </div>
              </>}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-3089" data-mp="nebkrh4gy0m3">
          <div className="flex flex-wrap items-center gap-4 text-gray-400 text-sm" data-id="element-3090" data-mp="nge97wpz3lop">
            <span className="flex items-center gap-1.5" data-id="element-3091" data-mp="rhq3wgnpex3r">
              <CalendarIcon className="w-4 h-4 text-haa-blue" data-id="element-3092" data-mp="3840x9nhp9abm" />
              <span className="font-nunito" data-id="element-3093" data-mp="3c5w231yua5u5">{translatedDate}</span>
            </span>
          </div>
          <a href={`#/news/${post.id}?lang=${language}`} className="inline-flex items-center gap-1.5 text-haa-blue hover:text-haa-midblue font-nunito font-bold text-xs transition-colors" title={isEs ? 'Abrir historia' : 'Open story'} data-id="element-3094" data-mp="1in5f1ysov6n2">
            <Share2Icon className="w-3.5 h-3.5" data-id="element-3095" data-mp="18v98dzcje878" />
            {isEs ? 'Compartir' : 'Share'}
          </a>
        </div>
      </div>

      <StoryModal isOpen={modalOpen} onClose={() => setModalOpen(false)} post={post} translatedTitle={translatedTitle} translatedDate={translatedDate} translatedFullStory={translatedFullStory} isEs={isEs} t={t} data-id="element-3096" data-mp="zx6x8qz39z68" />
    </motion.article>;
}
// Regular Card
function PostCard({
  post,
  index
}: {
  post: BlogPost;
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const [modalOpen, setModalOpen] = useState(false);
  const {
    t,
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const postNum = getPostNum(post.id);
  const translatedTitle = resolveTranslatedField(t, postNum, 'title', post.title);
  const translatedExcerpt = resolveTranslatedField(t, postNum, 'excerpt', post.excerpt);
  const translatedDate = resolveTranslatedField(t, postNum, 'date', post.date);
  const translatedReadTime = resolveTranslatedField(t, postNum, 'readTime', post.readTime);
  const translatedFullStory = resolveTranslatedField(t, postNum, 'fullStory', post.fullStory);
  const translatedCategory = translateCategory(t, post.category);
  const isSportsComposite = post.id === 'post-8' || post.players && post.players.length > 0;
  const isWabafu = post.id === 'post-12';
  const compositeImages = SPORTS_COMPOSITE_IMAGES;
  const hasMultipleImages = !!post.secondImage;
  return <motion.article ref={ref} initial={{
    opacity: 0,
    y: 30
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay: index * 0.1,
    ease: [0.16, 1, 0.3, 1]
  }} className="bg-white rounded-2xl overflow-hidden shadow-lg group flex flex-col" data-id="element-3097" data-mp="3ua8n6gniyj0m">
      {isSportsComposite ? <div className="relative h-64 overflow-hidden flex-shrink-0 bg-gradient-to-b from-haa-midblue via-haa-blue to-haa-navy" data-id="element-3098" data-mp="1b2bllxi34tmb">
          {/* Player Images */}
          <div className="absolute inset-0 flex items-end justify-center px-2 pb-12 sm:pb-10" data-id="element-3099" data-mp="1l2lz7j818320">
            {compositeImages.map((img, i) => <motion.div key={img.name} initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2 + i * 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="relative w-1/5 -mx-1.5 sm:-mx-2" data-id="element-3100" data-mp="1yvbnjub19k2a" data-mpi={i}>
                <img src={img.url} alt={img.name} className="w-full h-auto object-contain drop-shadow-2xl" data-id="element-3101" data-mp="2diad1jqby4ed" />
              </motion.div>)}
          </div>
          {/* Gradient Overlay */}
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-haa-navy/80 to-transparent" data-id="element-3102" data-mp="29fvu6zmnv4n3" />
          {/* Top Left Badge */}
          <div className="absolute top-3 left-3" data-id="element-3103" data-mp="1zo9mvedg1ppm">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3104" data-mp="1zp16dqtrnwzj">
              {translatedCategory}
            </span>
          </div>
          {/* Bottom Text */}
          <div className="absolute bottom-0 left-0 right-0 p-5 pb-3" data-id="element-3105" data-mp="3sda8bo37fh8j">
            <p className="text-white/90 font-nunito text-[10px] sm:text-xs font-bold tracking-widest mb-1 flex items-center gap-1.5" data-id="element-3106" data-mp="3nmabeiu34z61">
              <span className="text-lg leading-none" data-id="element-3107" data-mp="3io8tw2cb57wo">🇭🇳</span>
              {isEs ? 'HONDUREÑOS EN LA MLS · TEMPORADA 2025-26' : 'HONDURANS IN MLS · 2025-26 SEASON'}
            </p>
          </div>
        </div> : isWabafu ? <div className="relative h-52 overflow-hidden flex-shrink-0" data-id="element-3108" data-mp="a7niyg0484qx">
          <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
        objectPosition: post.imagePosition ?? 'center center'
      }} data-id="element-3109" data-mp="a5b1e3y9rwee" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" data-id="element-3110" data-mp="2pwffmgft6wi4" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-id="element-3111" data-mp="2lzb0ook7hi1m">
            <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} animate={inView ? {
          scale: 1,
          opacity: 1
        } : {}} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-3112" data-mp="11d2y09880yfi">
              <span className="text-5xl mb-3 block" data-id="element-3113" data-mp="1p35km9td9c6q">🥁</span>
              <p className="font-bebas text-white text-2xl sm:text-3xl tracking-wider leading-tight" data-id="element-3114" data-mp="1t6bojat0ara1">
                {isEs ? 'CULTURA EN MOVIMIENTO' : 'CULTURE IN MOTION'}
              </p>
              <p className="font-nunito text-white/70 text-xs tracking-widest uppercase mt-1" data-id="element-3115" data-mp="3uxgf03j7dlss">
                Wabafu Garifuna Dance Theater
              </p>
            </motion.div>
          </div>
          <div className="absolute top-3 left-3" data-id="element-3116" data-mp="25oo4isx55496">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3117" data-mp="25nczkprylqgh">
              {translatedCategory}
            </span>
          </div>
        </div> : hasMultipleImages ? <div className="flex-shrink-0" data-id="element-3118" data-mp="2agfmif28e1np">
          <div className="grid grid-cols-2 gap-0.5" data-id="element-3119" data-mp="2ahh6xa5500i3">
            <div className="relative h-36 overflow-hidden" data-id="element-3120" data-mp="1mqv17pilusb9">
              <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
            objectPosition: post.imagePosition ?? 'center 15%'
          }} data-id="element-3121" data-mp="1io85hditcn8s" />
            </div>
            <div className="relative h-36 overflow-hidden" data-id="element-3122" data-mp="381v3p1f3d2kv">
              <img src={post.secondImage!} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
            objectPosition: 'center center'
          }} data-id="element-3123" data-mp="334bp20ehense" />
            </div>
          </div>
          <div className="relative -mt-8 px-3 pb-0 z-10" data-id="element-3124" data-mp="fx6zto4ib23j">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3125" data-mp="ku75hxxg22u4">
              {translatedCategory}
            </span>
          </div>
        </div> : <div className="relative h-44 overflow-hidden flex-shrink-0" data-id="element-3126" data-mp="1cnpoquunsgy1">
          {post.image ? <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
        objectPosition: post.imagePosition ?? 'center 15%'
      }} data-id="element-3127" data-mp="ysnkec5gmznc" /> : <div className={`w-full h-full bg-gradient-to-br ${getGradient(post.category)}`} data-id="element-3128" data-mp="1m2po7mrk2cxr">
              <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, white 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }} data-id="element-3129" data-mp="1r3534irhjshu" />
            </div>}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" data-id="element-3130" data-mp="24vtm5kbt3by6" />
          <div className="absolute top-3 left-3" data-id="element-3131" data-mp="29s1vgh3boaxf">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3132" data-mp="1w1yeo5n7p9fd">
              {translatedCategory}
            </span>
          </div>
        </div>}

      <div className="p-5 flex flex-col flex-1" data-id="element-3133" data-mp="2nqrsjwq56als">
        <div className="flex items-center gap-2 mb-2" data-id="element-3134" data-mp="2skctnxn17kp7">
          <div className="w-4 h-0.5 bg-haa-blue rounded-full" data-id="element-3135" data-mp="h4cds770br5m" />
          <span className="font-nunito text-gray-400 text-xs" data-id="element-3136" data-mp="h2rfc6hkno2h">
            {translatedDate}
          </span>
        </div>

        {!isSportsComposite && <h3 className="font-bebas text-xl text-haa-navy tracking-wider leading-tight mb-2" data-id="element-3137" data-mp="quou1n7sgy5y">
            {translatedTitle}
          </h3>}

        <div className="flex-1 mb-3" data-id="element-3138" data-mp="lvuc0nhnbdmw">
          {!isSportsComposite && <p className="font-nunito text-gray-500 text-sm leading-relaxed line-clamp-3" data-id="element-3139" data-mp="3ubnkqg5qjkte">
              {translatedExcerpt}
            </p>}
        </div>

        <div className="mb-4" data-id="element-3140" data-mp="8474tsm60kpc">
          {post.storyUrl && post.storyUrl !== '#' ? <a href={post.storyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-haa-blue font-nunito font-bold text-sm hover:text-haa-midblue transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue rounded self-start" data-id="element-3141" data-mp="34ujjcaxzlov">
              {t('news.readFullStory')}
              <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-3142" data-mp="1qlr95goip6b3" />
            </a> : post.fullStory || post.players || post.videoEmbeds ? <button onClick={() => setModalOpen(true)} className="inline-flex items-center gap-1.5 text-haa-blue font-nunito font-bold text-sm hover:text-haa-midblue transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue rounded self-start" data-id="element-3143" data-mp="1vlncretayvq4">
              {t('news.readFullStory')}
              <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-3144" data-mp="1cuw6wn8ixngh" />
            </button> : null}
        </div>

        {post.coverageLinks && post.coverageLinks.length > 0 && <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-3" data-id="element-3145" data-mp="1hn5uhwm62bfl">
            <NewspaperIcon className="w-3 h-3 text-gray-400 flex-shrink-0" data-id="element-3146" data-mp="k5oymwj9pji1" />
            <span className="font-nunito text-gray-400 text-xs uppercase tracking-wider font-semibold" data-id="element-3147" data-mp="p3qsmci8f38a">
              {t('news.alsoCoveredBy')}
            </span>
            {post.coverageLinks.map((link, i) => <Fragment key={i} data-id="element-3148" data-mp="2ye7yout9mrx5" data-mpi={i}>
                {i > 0 && <span className="text-gray-200 select-none" data-id="element-3149" data-mp="2ted7eq5kw0bk">·</span>}
                <a href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-nunito text-xs font-bold text-haa-midblue hover:text-haa-blue hover:underline transition-colors" data-id="element-3150" data-mp="2tgq0nzgsdx3x">
                  {link.label}
                  <ExternalLinkIcon className="w-3 h-3" data-id="element-3151" data-mp="2anm3w589v453" />
                </a>
              </Fragment>)}
          </div>}
      </div>

      <div className="flex items-center justify-between px-5 pb-5 pt-3 border-t border-gray-100 mt-auto" data-id="element-3152" data-mp="25qvl3a531ufz">
        <div className="flex flex-wrap items-center gap-3 text-gray-400 text-xs" data-id="element-3153" data-mp="20s26tfhz1zer">
          <span className="flex items-center gap-1" data-id="element-3154" data-mp="1vrmxr04q3e2l">
            <CalendarIcon className="w-3.5 h-3.5 text-haa-blue" data-id="element-3155" data-mp="3vskulxcbowhr" />
            <span className="font-nunito" data-id="element-3156" data-mp="3vvgrrjgcjh55">{translatedDate}</span>
          </span>
        </div>
        <a href={`#/news/${post.id}?lang=${language}`} className="inline-flex items-center gap-1 text-haa-blue hover:text-haa-midblue font-nunito font-bold text-xs transition-colors" title={isEs ? 'Abrir historia' : 'Open story'} data-id="element-3157" data-mp="4ow2a45xwha8">
          <Share2Icon className="w-3.5 h-3.5" data-id="element-3158" data-mp="3d1tk2sgln01s" />
          {isEs ? 'Compartir' : 'Share'}
        </a>
      </div>

      <StoryModal isOpen={modalOpen} onClose={() => setModalOpen(false)} post={post} translatedTitle={translatedTitle} translatedDate={translatedDate} translatedFullStory={translatedFullStory} isEs={isEs} t={t} data-id="element-3159" data-mp="3i30uz0e1gfco" />
    </motion.article>;
}
// Main Section
export function NewsBlog() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const [activeCategory, setActiveCategory] = useState('All');
  const {
    t
  } = useLanguage();
  const filtered = activeCategory === 'All' ? BLOG_POSTS : BLOG_POSTS.filter(p => p.category === activeCategory);
  const featured = filtered.find(p => p.featured) ?? null;
  const rest = filtered.filter(p => p.id !== featured?.id).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const categoryTranslations: Record<string, string> = {
    All: t('news.catAll'),
    Political: t('news.catPolitical'),
    Immigration: t('news.catImmigration'),
    Business: t('news.catBusiness'),
    'Note Worthy': t('news.catNoteWorthy'),
    Events: t('news.catEvents'),
    'Arts & Entertainment': t('news.catArtMedia'),
    Sports: t('news.catSports')
  };
  return <section id="news" className="bg-[#F8FAFF] py-16 lg:py-24" aria-labelledby="news-heading" data-id="element-3160" data-mp="1p5wvxvyne7gw">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3161" data-mp="1k7byg4as61w5">
        <div ref={titleRef} className="mb-10" data-id="element-3162" data-mp="3k8s93c136h6z">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} className="flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-3163" data-mp="3k9v1joe24rp0">
            <div data-id="element-3164" data-mp="bzrzwffdp7l2">
              <span className="font-nunito text-haa-midblue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-3165" data-mp="bza5loca13fs">
                {t('news.label')}
              </span>
              <h2 id="news-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2 leading-none" data-id="element-3166" data-mp="ktetq00100rm">
                {t('news.heading')}
              </h2>
              <div className="flex items-center gap-3 mt-4" data-id="element-3167" data-mp="urkrq6ipomr7">
                <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-3168" data-mp="14vj69ouo8zg8" />
                <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-3169" data-mp="14sn8q1mqkcsb" />
                <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-3170" data-mp="zulmj7u0jxcm" />
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.15
      }} className="flex flex-wrap gap-2 mb-10" role="tablist" aria-label="Filter by category" data-id="element-3171" data-mp="qvi3u4yrdjyn">
          {ALL_CATEGORIES.map(cat => <button key={cat} role="tab" aria-selected={activeCategory === cat} onClick={() => setActiveCategory(cat)} className={`relative font-nunito font-semibold text-sm px-5 py-2 rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue ${activeCategory === cat ? 'bg-haa-navy text-white shadow-md' : 'bg-white text-gray-500 hover:text-haa-navy border border-gray-200 hover:border-haa-navy'}`} data-id="element-3172" data-mp="26c8v3rksdl8a">
              <span className="inline-flex items-center gap-1.5" data-id="element-3173" data-mp="2gahe1b0fogy8">
                {cat === 'Sports' && <TrophyIcon className="w-3.5 h-3.5" data-id="element-3174" data-mp="340bsfx0eidkl" />}
                {categoryTranslations[cat] ?? cat}
              </span>
            </button>)}
        </motion.div>

        <AnimatePresence mode="wait" data-id="element-3175" data-mp="2u0kwrs03tsix">
          <motion.div key={activeCategory} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.3
        }} data-id="element-3176" data-mp="2u3p8rbjt9sk5">
            {filtered.length === 0 ? <div className="text-center py-20" data-id="element-3177" data-mp="p2usw99rs1a">
                <TagIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" data-id="element-3178" data-mp="oay1i3xro9f" />
                <p className="font-nunito text-gray-400 text-lg" data-id="element-3179" data-mp="enkhqrrcp0i5">
                  {t('news.noPosts')}
                </p>
              </div> : activeCategory === 'All' ? <>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3180" data-mp="3j3lk8edirdmg">
                  {featured && <FeaturedCard post={featured} data-id="element-3181" data-mp="3e4ih4zh3nhh4" />}
                  {rest.length > 0 && <div className="flex flex-col gap-6" data-id="element-3182" data-mp="3rvdqsdzfa2lr">
                      {rest.slice(0, 2).map((post, i) => <PostCard key={post.id} post={post} index={i} data-id="element-3183" data-mp="3n3lzu2cctuv3" data-mpi={i} />)}
                    </div>}
                </div>
                {rest.length > 2 && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6" data-id="element-3184" data-mp="2vcg6q5v1wxfb">
                    {rest.slice(2).map((post, i) => <PostCard key={post.id} post={post} index={i + 2} data-id="element-3185" data-mp="1np0md82oonsm" data-mpi={i} />)}
                  </div>}
              </> : <div className={`grid gap-6 ${filtered.length === 1 ? 'grid-cols-1 max-w-3xl mx-auto' : 'grid-cols-1 sm:grid-cols-2'}`} data-id="element-3186" data-mp="1jnnlzdid1ct3">
                {filtered.map((post, i) => <PostCard key={post.id} post={post} index={i} data-id="element-3187" data-mp="2bc8sttbum2w8" data-mpi={i} />)}
              </div>}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}